/**
 * Dynamic pizza sizes — FRONTEND.md §14.6 / BACKEND.md §14.6.
 *
 * Ported from legacy `lib/pizza-config-store.ts`. It lives in `packages/shared`
 * because two modules need it: the pizza configurator in `manager-menu` and the
 * pizza builder inside the order builder in `manager-orders`.
 *
 * The load-bearing part is `ingredientSizeMap`. A pizza ingredient carries its
 * prices twice: in the **legacy `price32`/`price50` columns**, which are NOT
 * NULL and therefore always present, and in the **`size_data` JSONB**, which is
 * where a franchise's real sizes live once it has more than two. `size_data`
 * overlays the legacy pair, field by field — not wholesale — because a
 * `size_data` entry may set a price and say nothing about weight.
 *
 * Every rule here about a missing value meaning "enabled" exists because
 * configurations saved before that flag was added do not carry it. Reading
 * absence as "off" would switch a live restaurant's sizes off.
 */

export interface PizzaSize {
  cm: number
  label: string
  basePrice: number
  /** Dough, sauce and base cheese — for the margin calculation. Optional. */
  baseCost?: number
  /** Missing means enabled: configs predate this flag. */
  enabled?: boolean
}

export interface PizzaBase {
  value: string
  label: string
  prices: Record<number, number>
}

export interface PizzaConfig {
  sizes: PizzaSize[]
  garlicCrustEnabled: boolean
  garlicCrustPrice: number
  /** Size used for a kombo pizza. Missing means the largest enabled size. */
  komboSizeCm?: number
  /** Missing means enabled: configs predate this flag. */
  komboEnabled?: boolean
  bases: PizzaBase[]
  /**
   * The base a new pizza opens with — `FRONTEND.md` §9.60, owner decision third round.
   *
   * Names a `PizzaBase.value`. Missing, empty, or naming a base that is not in the list means **no
   * preselection**, which is what every config did before this field existed.
   */
  defaultBaseValue?: string | null
}

export interface IngredientSizeValues {
  price: number
  weightG: number
  unitLabel: string | null
  cost: number | null
}

/** What an ingredient row looks like, from either storage generation. */
export interface PizzaIngredientLike {
  price32?: number | string | null
  price50?: number | string | null
  weightG32?: number | null
  weightG50?: number | null
  unitLabel32?: string | null
  unitLabel50?: string | null
  cost32?: number | string | null
  cost50?: number | string | null
  sizeData?: Record<
    string,
    { price?: number; weightG?: number; unitLabel?: string | null; cost?: number | null }
  > | null
}

export const DEFAULT_PIZZA_CONFIG: PizzaConfig = {
  sizes: [
    { cm: 32, label: '32 cm', basePrice: 7.9 },
    { cm: 50, label: '50 cm', basePrice: 11.9 },
  ],
  garlicCrustEnabled: true,
  garlicCrustPrice: 0.5,
  bases: [
    { value: 'rajcinovy', label: 'Rajčinový základ', prices: { 32: 0, 50: 0 } },
    { value: 'smotanovy', label: 'Smotanový základ', prices: { 32: 0, 50: 0 } },
  ],
}

/** Enabled sizes, smallest first. A size with no `enabled` flag counts as on. */
export function enabledSizes(config: PizzaConfig | null | undefined): PizzaSize[] {
  return (config?.sizes ?? [])
    .filter((size) => size && Number.isFinite(size.cm) && size.enabled !== false)
    .sort((left, right) => left.cm - right.cm)
}

export function isKomboEnabled(config: PizzaConfig | null | undefined): boolean {
  return config?.komboEnabled !== false
}

/** The configured kombo size, or the largest enabled one, or 50. */
export function komboSizeCm(config: PizzaConfig | null | undefined): number {
  if (config?.komboSizeCm && Number.isFinite(config.komboSizeCm)) return config.komboSizeCm
  const sizes = enabledSizes(config)
  return sizes.length > 0 ? (sizes[sizes.length - 1]?.cm ?? 50) : 50
}

/** `"Vlastná pizza – 45 cm (…)"` → `45`. */
export function sizeCmFromName(name: string): number | null {
  const match = /(\d{2,3})\s*cm/i.exec(name)
  if (!match?.[1]) return null
  const cm = Number.parseInt(match[1], 10)
  return Number.isFinite(cm) ? cm : null
}

function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  return Number.isFinite(Number(value)) ? Number(value) : null
}

/**
 * Every size this ingredient has values for: the legacy 32/50 pair first, then
 * `size_data` laid over it field by field.
 */
export function ingredientSizeMap(
  ingredient: PizzaIngredientLike,
): Map<number, IngredientSizeValues> {
  const bySize = new Map<number, IngredientSizeValues>()

  const price32 = toNumberOrNull(ingredient.price32)
  if (price32 !== null || ingredient.weightG32 != null) {
    bySize.set(32, {
      price: price32 ?? 0,
      weightG: ingredient.weightG32 ?? 0,
      unitLabel: ingredient.unitLabel32 ?? null,
      cost: toNumberOrNull(ingredient.cost32),
    })
  }

  const price50 = toNumberOrNull(ingredient.price50)
  if (price50 !== null || ingredient.weightG50 != null) {
    bySize.set(50, {
      price: price50 ?? 0,
      weightG: ingredient.weightG50 ?? 0,
      unitLabel: ingredient.unitLabel50 ?? null,
      cost: toNumberOrNull(ingredient.cost50),
    })
  }

  for (const [key, entry] of Object.entries(ingredient.sizeData ?? {})) {
    const cm = Number.parseInt(key, 10)
    if (!Number.isFinite(cm)) continue

    const existing = bySize.get(cm)
    bySize.set(cm, {
      price: entry.price ?? existing?.price ?? 0,
      weightG: entry.weightG ?? existing?.weightG ?? 0,
      // `undefined` means "said nothing"; an explicit null means "cleared".
      unitLabel: entry.unitLabel !== undefined ? (entry.unitLabel ?? null) : (existing?.unitLabel ?? null),
      cost: entry.cost !== undefined ? (entry.cost ?? null) : (existing?.cost ?? null),
    })
  }

  return bySize
}

/**
 * The ingredient's values at one size: an exact match, otherwise the nearest
 * size that has data. Ties go to the larger size.
 *
 * The fallback is what keeps a franchise's 45 cm pizza priceable when its
 * ingredients were only ever costed at 32 and 50 — and it is why the legacy
 * NOT NULL columns can still be derived when neither 32 nor 50 is enabled.
 */
export function ingredientForSize(
  ingredient: PizzaIngredientLike,
  cm: number,
): IngredientSizeValues {
  const bySize = ingredientSizeMap(ingredient)

  const exact = bySize.get(cm)
  if (exact) return exact

  let nearest: { cm: number; values: IngredientSizeValues } | null = null
  for (const [candidateCm, values] of bySize) {
    const candidateDistance = Math.abs(candidateCm - cm)
    if (!nearest) {
      nearest = { cm: candidateCm, values }
      continue
    }
    const nearestDistance = Math.abs(nearest.cm - cm)
    if (
      candidateDistance < nearestDistance ||
      (candidateDistance === nearestDistance && candidateCm > nearest.cm)
    ) {
      nearest = { cm: candidateCm, values }
    }
  }

  return nearest?.values ?? { price: 0, weightG: 0, unitLabel: null, cost: null }
}

/** A base saved before per-size pricing carried a single `price`. */
export function migrateBases(bases: PizzaBase[]): PizzaBase[] {
  return bases.map((base) => {
    if (base.prices) return base
    const legacyPrice = (base as unknown as { price?: number }).price ?? 0
    return { value: base.value, label: base.label, prices: { 32: legacyPrice, 50: legacyPrice } }
  })
}

/** Fills a stored config out to a complete one, migrating what needs it. */
export function normalisePizzaConfig(stored: Partial<PizzaConfig> | null | undefined): PizzaConfig {
  const merged = { ...DEFAULT_PIZZA_CONFIG, ...(stored ?? {}) }
  merged.bases =
    !merged.bases || merged.bases.length === 0
      ? DEFAULT_PIZZA_CONFIG.bases
      : migrateBases(merged.bases)
  return merged
}
